import { useEffect, useState } from "react";
import { X, Save, Mail, Phone, Calendar as CalendarIcon, CreditCard, MapPin, Hash } from "lucide-react";
import { toast } from "sonner";
import { adminListBookings, adminUpdateBooking } from "../../lib/api";

const STATUS_STYLES = {
  paid: "bg-[var(--ed-emerald-tint)] text-[var(--ed-emerald)]",
  pending_payment: "bg-amber-50 text-amber-700 border-amber-200",
  cancelled: "bg-red-50 text-red-600 border-red-200",
  expired: "bg-[var(--ed-paper)] text-[var(--ed-ink-muted)]",
  inquiry: "bg-blue-50 text-blue-700 border-blue-200",
  initiated: "bg-[var(--ed-paper)] text-[var(--ed-ink-muted)]",
};

function StatusPill({ status }) {
  const cls = STATUS_STYLES[status] || "bg-[var(--ed-paper)] text-[var(--ed-ink-muted)]";
  return (
    <span className={`text-[0.55rem] uppercase tracking-[0.2em] px-2 py-1 border ${cls}`}>
      {(status || "—").replace(/_/g, " ")}
    </span>
  );
}

export default function BookingsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try { setRows(await adminListBookings()); }
    catch { toast.error("Could not load bookings"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = rows.filter((b) => {
    if (filter === "all") return true;
    if (filter === "paid") return b.status === "paid";
    if (filter === "pending") return b.status === "pending_payment";
    if (filter === "inquiry") return b.status === "inquiry" || (!b.status);
    if (filter === "cancelled") return b.status === "cancelled";
    return true;
  });

  const counts = {
    all: rows.length,
    paid: rows.filter((b) => b.status === "paid").length,
    pending: rows.filter((b) => b.status === "pending_payment").length,
    inquiry: rows.filter((b) => b.status === "inquiry" || !b.status).length,
    cancelled: rows.filter((b) => b.status === "cancelled").length,
  };

  return (
    <div data-testid="bookings-panel">
      <header className="mb-8">
        <p className="ed-overline mb-2">Operations</p>
        <h1 className="font-serif text-3xl sm:text-4xl text-[var(--ed-ink)]">Bookings</h1>
        <p className="text-[var(--ed-ink-muted)] text-sm mt-1.5">{rows.length} total · click any row to view guest, payment and internal notes.</p>
      </header>

      <div className="flex flex-wrap gap-2 mb-6 pb-5 border-b border-[var(--ed-rule)]">
        {[
          ["all", `All · ${counts.all}`],
          ["paid", `Paid · ${counts.paid}`],
          ["pending", `Pending · ${counts.pending}`],
          ["inquiry", `Inquiries · ${counts.inquiry}`],
          ["cancelled", `Cancelled · ${counts.cancelled}`],
        ].map(([k, l]) => (
          <button
            key={k}
            data-testid={`bk-filter-${k}`}
            onClick={() => setFilter(k)}
            className={`text-[0.65rem] uppercase tracking-[0.22em] px-4 py-2 border rounded-sm transition-colors ${
              filter === k
                ? "bg-[var(--ed-ink)] text-[var(--ed-bg)] border-[var(--ed-ink)]"
                : "border-[var(--ed-rule)] text-[var(--ed-ink-soft)] hover:border-[var(--ed-ink)] hover:text-[var(--ed-ink)]"
            }`}
          >{l}</button>
        ))}
      </div>

      {loading ? (
        <p className="text-[var(--ed-ink-muted)] py-16 text-center">Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-[var(--ed-rule)] rounded-sm p-16 text-center">
          <p className="text-[var(--ed-ink-muted)]">No bookings yet in this view.</p>
        </div>
      ) : (
        <div className="border border-[var(--ed-rule)] rounded-sm overflow-x-auto bg-[var(--ed-bg)]" data-testid="admin-bookings">
          <table className="w-full text-sm">
            <thead className="bg-[var(--ed-paper)] text-[var(--ed-ink-muted)] text-[0.6rem] uppercase tracking-[0.22em]">
              <tr>
                <th className="text-left px-5 py-3">Created</th>
                <th className="text-left px-5 py-3">Guest</th>
                <th className="text-left px-5 py-3">Property</th>
                <th className="text-left px-5 py-3">Dates</th>
                <th className="text-left px-5 py-3">Amount</th>
                <th className="text-left px-5 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((b) => (
                <tr
                  key={b.id}
                  data-testid={`booking-row-${b.id}`}
                  onClick={() => setSelected(b)}
                  className="border-t border-[var(--ed-rule)] text-[var(--ed-ink-soft)] cursor-pointer hover:bg-[var(--ed-paper)]/60 transition-colors"
                >
                  <td className="px-5 py-3.5 text-[var(--ed-ink-muted)] text-xs whitespace-nowrap">{(b.created_at || "").slice(0, 10)}</td>
                  <td className="px-5 py-3.5">
                    <p className="text-[var(--ed-ink)]">{b.full_name}</p>
                    <p className="text-[var(--ed-ink-muted)] text-xs">{b.email}</p>
                  </td>
                  <td className="px-5 py-3.5">{b.property_name || "—"}</td>
                  <td className="px-5 py-3.5 text-xs whitespace-nowrap">{b.check_in} → {b.check_out}</td>
                  <td className="px-5 py-3.5 text-[var(--ed-emerald)] font-medium">{b.amount ? `£${b.amount.toFixed(0)}` : "—"}</td>
                  <td className="px-5 py-3.5"><StatusPill status={b.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <BookingDetailModal
          booking={selected}
          onClose={() => setSelected(null)}
          onSaved={() => { load(); setSelected(null); }}
        />
      )}
    </div>
  );
}

function BookingDetailModal({ booking, onClose, onSaved }) {
  const [notes, setNotes] = useState(booking.notes || "");
  const [status, setStatus] = useState(booking.status || "");
  const [saving, setSaving] = useState(false);

  const onSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (notes !== (booking.notes || "")) payload.notes = notes;
      if (status !== (booking.status || "")) payload.status = status;
      if (Object.keys(payload).length === 0) { toast("Nothing changed"); setSaving(false); return; }
      await adminUpdateBooking(booking.id, payload);
      toast.success("Booking updated");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto">
      <div data-testid="booking-detail" className="w-full max-w-2xl my-8 bg-[var(--ed-bg)] border border-[var(--ed-rule)] rounded-sm shadow-2xl">
        <div className="px-7 pt-6 pb-5 border-b border-[var(--ed-rule)] flex items-start justify-between gap-4">
          <div>
            <p className="ed-overline mb-1">Booking</p>
            <h2 className="font-serif text-2xl text-[var(--ed-ink)]">{booking.full_name}</h2>
            <div className="mt-2 flex items-center gap-3">
              <StatusPill status={booking.status} />
              {booking.amount && (
                <span className="text-[var(--ed-emerald)] font-medium text-sm">£{booking.amount.toFixed(0)}</span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-[var(--ed-ink-muted)] hover:text-[var(--ed-ink)] p-1"><X size={20} /></button>
        </div>

        <div className="p-7 space-y-6">
          {/* Guest */}
          <section>
            <p className="ed-overline mb-3">Guest</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <Info icon={Mail}  label="Email" value={booking.email} link={`mailto:${booking.email}`} />
              <Info icon={Phone} label="Phone" value={booking.phone || "—"} link={booking.phone ? `tel:${booking.phone}` : null} />
              <Info icon={Hash}  label="Guests" value={booking.guests || "—"} />
              <Info icon={MapPin} label="Property" value={booking.property_name || "—"} />
            </div>
          </section>

          {/* Stay */}
          <section>
            <p className="ed-overline mb-3">Stay</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <Info icon={CalendarIcon} label="Check-in"  value={booking.check_in || "—"} />
              <Info icon={CalendarIcon} label="Check-out" value={booking.check_out || "—"} />
            </div>
            {booking.message && (
              <div className="mt-4 px-4 py-3 bg-[var(--ed-paper)] border border-[var(--ed-rule)] text-sm text-[var(--ed-ink-soft)] rounded-sm whitespace-pre-wrap">
                <p className="text-[0.6rem] uppercase tracking-[0.22em] text-[var(--ed-ink-muted)] mb-1.5">Guest message</p>
                {booking.message}
              </div>
            )}
          </section>

          {/* Payment */}
          <section>
            <p className="ed-overline mb-3">Payment</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <Info icon={CreditCard} label="Booking status" value={(booking.status || "—").replace(/_/g, " ")} />
              <Info icon={CreditCard} label="Payment status" value={(booking.payment_status || "—").replace(/_/g, " ")} />
              <Info icon={Hash} label="Stripe session" value={booking.stripe_session_id ? booking.stripe_session_id.slice(0, 22) + "…" : "—"} mono />
              <Info icon={Hash} label="Booking ID" value={booking.id?.slice(0, 8)} mono />
            </div>
          </section>

          {/* Admin override */}
          <section>
            <p className="ed-overline mb-2">Override status</p>
            <select
              data-testid="booking-status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full bg-[var(--ed-bg)] border border-[var(--ed-rule)] focus:border-[var(--ed-emerald)] text-[var(--ed-ink)] px-3 py-2.5 text-sm outline-none rounded-sm"
            >
              <option value="">— Keep current ({booking.status || "—"}) —</option>
              <option value="paid">Paid</option>
              <option value="pending_payment">Pending payment</option>
              <option value="confirmed">Confirmed</option>
              <option value="cancelled">Cancelled</option>
              <option value="inquiry">Inquiry</option>
              <option value="expired">Expired</option>
            </select>
          </section>

          {/* Notes */}
          <section>
            <p className="ed-overline mb-2">Internal notes</p>
            <textarea
              data-testid="booking-notes"
              rows={5}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Private notes — only visible inside the studio."
              className="w-full bg-[var(--ed-bg)] border border-[var(--ed-rule)] focus:border-[var(--ed-emerald)] text-[var(--ed-ink)] px-3 py-2.5 text-sm outline-none rounded-sm resize-none"
            />
          </section>
        </div>

        <div className="px-7 py-5 border-t border-[var(--ed-rule)] bg-[var(--ed-paper)]/40 flex items-center justify-end gap-2">
          <button onClick={onClose} className="text-[0.65rem] uppercase tracking-[0.22em] px-4 py-2.5 border border-[var(--ed-rule)] text-[var(--ed-ink-soft)] hover:border-[var(--ed-ink)] hover:text-[var(--ed-ink)] rounded-sm">
            Close
          </button>
          <button
            data-testid="booking-save"
            onClick={onSave}
            disabled={saving}
            className="text-[0.65rem] uppercase tracking-[0.22em] px-5 py-2.5 bg-[var(--ed-emerald)] text-white hover:bg-[var(--ed-emerald-deep)] rounded-sm flex items-center gap-2 disabled:opacity-50"
          >
            <Save size={12} /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Info({ icon: Icon, label, value, link, mono }) {
  const content = (
    <div className="flex items-start gap-3 px-3 py-2.5 border border-[var(--ed-rule)] rounded-sm bg-[var(--ed-paper)]/30">
      <Icon size={14} className="text-[var(--ed-ink-muted)] mt-0.5 flex-shrink-0" />
      <div className="min-w-0">
        <p className="text-[0.55rem] uppercase tracking-[0.22em] text-[var(--ed-ink-muted)] mb-0.5">{label}</p>
        <p className={`text-[var(--ed-ink)] truncate ${mono ? "font-mono text-xs" : ""}`}>{value || "—"}</p>
      </div>
    </div>
  );
  if (link) return <a href={link} className="hover:opacity-80">{content}</a>;
  return content;
}
